import { apiClient } from '@/api/client'
import {
  companyTimeCorrectionRequestsPath,
  companyTimeCorrectionRequestByIdPath,
  companyTimeCorrectionRequestApprovePath,
  companyTimeCorrectionRequestRejectPath,
  ATTENDANCE_ME_TIME_CORRECTIONS,
  TIME_CORRECTIONS,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type {
  TimeCorrectionRequest,
  CreateTimeCorrectionRequestDto,
  MyTimeCorrectionRequestListQuery,
  RejectTimeCorrectionRequestDto,
  ApproveTimeCorrectionRequestDto,
  TimeCorrectionRequestListResponse,
  TimeCorrectionRequestDetailResponse,
} from '@/features/attendance/types/timeCorrection.types'

interface ListPayload {
  data: TimeCorrectionRequest[]
  pagination: { total: number; page: number; limit: number }
  serverTime?: string
}

type DetailPayload =
  | TimeCorrectionRequest
  | { data: TimeCorrectionRequest; serverTime?: string }

function isTimeCorrectionRequest(value: unknown): value is TimeCorrectionRequest {
  return (
    typeof value === 'object' &&
    value !== null &&
    '_id' in value &&
    'status' in value
  )
}

function toDetailResponse(payload: DetailPayload | undefined): TimeCorrectionRequestDetailResponse {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Time correction request not found')
  }
  if ('data' in payload && isTimeCorrectionRequest(payload.data)) {
    return { data: payload.data, serverTime: payload.serverTime }
  }
  if (isTimeCorrectionRequest(payload)) {
    return { data: payload }
  }
  throw new Error('Time correction request not found')
}

function toListResponse(
  payload: ListPayload,
  fallbackLimit: number
): TimeCorrectionRequestListResponse {
  return {
    data: Array.isArray(payload.data) ? payload.data : [],
    pagination: payload.pagination ?? { total: 0, page: 1, limit: fallbackLimit },
    serverTime: payload.serverTime,
  }
}

export async function createTimeCorrectionRequest(
  companyId: string,
  body: CreateTimeCorrectionRequestDto
): Promise<TimeCorrectionRequestDetailResponse> {
  const { data } = await apiClient.post<
    ApiResponseWrapper<TimeCorrectionRequest>
  >(companyTimeCorrectionRequestsPath(companyId), body)
  const inner = data?.data
  if (!inner) throw new Error('Invalid time correction request response')
  return { data: inner }
}

const DEFAULT_PAGE_LIMIT = 10
const MAX_PAGE_LIMIT = 100

export async function getTimeCorrectionsWorkspace(
  query?: MyTimeCorrectionRequestListQuery
): Promise<TimeCorrectionRequestListResponse> {
  const rawLimit = query?.limit ?? DEFAULT_PAGE_LIMIT
  const limit = Math.min(Math.max(1, rawLimit), MAX_PAGE_LIMIT)
  const { companyIds, ...rest } = query ?? {}
  const params: Record<string, unknown> = {
    ...rest,
    page: rest.page ?? 1,
    limit,
  }
  if (companyIds && companyIds.length > 0) {
    params.companyIds = companyIds.join(',')
  }
  const { data } = await apiClient.get<ApiResponseWrapper<ListPayload>>(
    TIME_CORRECTIONS,
    { params }
  )
  const payload = data?.data
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return toListResponse(payload, limit)
  }
  return toListResponse(
    { data: [], pagination: { total: 0, page: 1, limit } },
    limit
  )
}

export async function getMyTimeCorrections(
  query?: MyTimeCorrectionRequestListQuery
): Promise<TimeCorrectionRequestListResponse> {
  const rawLimit = query?.limit ?? DEFAULT_PAGE_LIMIT
  const limit = Math.min(Math.max(1, rawLimit), MAX_PAGE_LIMIT)
  const { companyIds, ...rest } = query ?? {}
  const params: Record<string, unknown> = {
    ...rest,
    page: rest.page ?? 1,
    limit,
  }
  if (companyIds && companyIds.length > 0) {
    params.companyIds = companyIds.join(',')
  }
  const { data } = await apiClient.get<ApiResponseWrapper<ListPayload>>(
    ATTENDANCE_ME_TIME_CORRECTIONS,
    { params }
  )
  const payload = data?.data
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return toListResponse(payload, limit)
  }
  return toListResponse(
    { data: [], pagination: { total: 0, page: 1, limit } },
    limit
  )
}

export async function getTimeCorrectionRequestById(
  companyId: string,
  requestId: string
): Promise<TimeCorrectionRequestDetailResponse> {
  const { data } = await apiClient.get<ApiResponseWrapper<DetailPayload>>(
    companyTimeCorrectionRequestByIdPath(companyId, requestId)
  )
  return toDetailResponse(data?.data)
}

export async function approveTimeCorrectionRequest(
  companyId: string,
  requestId: string,
  body: ApproveTimeCorrectionRequestDto = {}
): Promise<TimeCorrectionRequestDetailResponse> {
  const { data } = await apiClient.patch<ApiResponseWrapper<DetailPayload>>(
    companyTimeCorrectionRequestApprovePath(companyId, requestId),
    body
  )
  return toDetailResponse(data?.data)
}

export async function rejectTimeCorrectionRequest(
  companyId: string,
  requestId: string,
  body: RejectTimeCorrectionRequestDto
): Promise<TimeCorrectionRequestDetailResponse> {
  const { data } = await apiClient.patch<ApiResponseWrapper<DetailPayload>>(
    companyTimeCorrectionRequestRejectPath(companyId, requestId),
    body
  )
  return toDetailResponse(data?.data)
}
